import { GoPerson, GoDependabot, GoQuestion } from "react-icons/go";

const TYPE_HUMAN = "human";
const TYPE_SERVICE = "service";

const STATUS_RETIRED = "retired";

/**
 * The account type registry.
 *
 * The API lists the accounts a target owns, whatever kind they are, and documents `type` as the
 * extension point — a future kind is a new value here, never a new endpoint and never a new page.
 * This lives in context/ rather than inside the Accounts section because the identity renderer needs
 * the same glyph for revision authors and member rows.
 *
 * `human` is in the table too: the human glyph is a lookup like any other, so no caller ever branches.
 */
const REGISTRY = {
  [TYPE_HUMAN]: {
    type: TYPE_HUMAN,
    label: "Person",
    pluralLabel: "People",
    glyph: GoPerson,
    description: "A person who signs in interactively.",
    creatable: false
  },
  [TYPE_SERVICE]: {
    type: TYPE_SERVICE,
    label: "Service Account",
    pluralLabel: "Service Accounts",
    glyph: GoDependabot,
    description: "A machine identity that holds its own API keys and authors content under its own name.",
    creatable: true
  }
};

export default class AccountTypes {
  static TYPE_HUMAN = TYPE_HUMAN;
  static TYPE_SERVICE = TYPE_SERVICE;
  static STATUS_RETIRED = STATUS_RETIRED;

  static typeOf(user) {
    return (user && user.type) ? user.type : TYPE_HUMAN;
  }

  /**
   * The registry entry for a user. An unknown type still renders — infra can introduce a type before
   * the console deploys — carrying its raw value as its label and a neutral glyph.
   */
  static of(user) {
    const type = AccountTypes.typeOf(user);
    if ( REGISTRY[type] ) {
      return REGISTRY[type];
    }
    return {
      type: type,
      label: type,
      pluralLabel: type,
      glyph: GoQuestion,
      description: "",
      creatable: false
    };
  }

  static isHuman(user) {
    return TYPE_HUMAN === AccountTypes.typeOf(user);
  }

  static isAccount(user) {
    return !AccountTypes.isHuman(user);
  }

  /**
   * `status` has exactly two values, "active" and "retired". The API's own readers default an
   * absent value to "active" for the migration window, so a record with no `status` reads as
   * active here too — only an explicit "retired" counts.
   */
  static isRetired(user) {
    return !!user && STATUS_RETIRED === user.status;
  }

  static creatableTypes() {
    return Object.values(REGISTRY).filter(entry => entry.creatable);
  }

  static defaultCreatableType() {
    const creatable = AccountTypes.creatableTypes();
    return 1 === creatable.length ? creatable[0].type : null;
  }

  /**
   * What the settings section calls itself. Always "Accounts" — the API's listing endpoint returns
   * whatever kinds of account a target owns, and the type is carried per row rather than in the
   * heading, so a second creatable type changes nothing here.
   */
  static sectionLabel() {
    return "Accounts";
  }
}
